import "../style.scss";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { InputAdornment, Tooltip } from "@mui/material";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import InfoTooltip from "components/shared/InfoTooltip";
import StyledTextField, { disabledTooltipStyle } from "components/shared/StyledTextField/StyledTextField";
import { TinyBody } from "helpers/TypographyComponents";
import type { FocusEventHandler } from "react";
import { forwardRef } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface NumberFormComponentProps {
    label: string;
    placeholder?: string;
    control: any;
    controlName: string;
    error?: any;
    required?: boolean;
    /**
     * If true, the field will accept float values
     */
    float?: boolean;
    id?: string;
    disabled?: boolean;
    disabledText?: string;
    /** When disabled, show the tooltip on the field hover instead of a lock icon adornment. */
    hideDisabledLockIcon?: boolean;
    helpdeskInfo?: { text: string; link: string };
    icon?: JSX.Element;
    /**
     * Show (optional) after field label
     */
    showOptionalMark?: boolean;
    /**
     * Container style { width: "100%" }
     */
    fullWidth?: boolean;
    sx?: any;
    labelStyle?: React.CSSProperties;
    infoTooltip?: { text: string; link?: string; type: "info" | "question" };
    loading?: boolean;
    onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    /**
     * For passing only number (or "") as argument for onBlur.
     */
    onBlur?: (value: number | string) => void;
    /**
     * Used when wanting to customise onBlur event. Passes whole event as argument.
     */
    onBlurWithEvent?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (value: number | string) => void;
    onChangeWithEvent?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    acceptsNegative?: boolean;
    customErrorMessage?: string;
    description?: string;
}

const NumberFormComponent = forwardRef<any, NumberFormComponentProps>(function NumberFormComponent(
    props: NumberFormComponentProps,
    ref,
) {
    const {
        label,
        float,
        placeholder,
        control,
        controlName,
        error,
        required,
        id,
        disabled,
        disabledText,
        hideDisabledLockIcon,
        helpdeskInfo,
        icon,
        showOptionalMark = false,
        fullWidth = false,
        sx,
        labelStyle,
        infoTooltip,
        loading,
        onFocus,
        onBlur,
        onBlurWithEvent,
        onChange,
        onChangeWithEvent,
        acceptsNegative = false,
        customErrorMessage = "",
        description = "",
    } = props;
    const [t] = useTranslation();

    const parseNumber = (input: string): number | "" => {
        const normalized = input.replace(",", ".").trim();
        if (normalized === "") return "";
        const parsed = float ? parseFloat(normalized) : parseInt(normalized, 10);
        return isNaN(parsed) ? "" : parsed;
    };

    return (
        <div className="form-component" style={fullWidth ? { width: "100%" } : {}}>
            <Controller
                name={controlName}
                control={control}
                rules={{
                    required: required ? t("NewElement.Required") : undefined,
                    validate: (content: string) => {
                        const value = acceptsNegative ? (content ?? 0).toString().replace("-", "") : content ?? "";
                        if (float) {
                            if (!acceptsNegative && (content ?? 0).toString().includes("-"))
                                return "Il n'est pas possible d'indiquer un nombre négatif.";
                            if (isNaN(+value.toString().replace(",", "."))) return t("NewElement.Number");
                            if (/\s/g.test(value)) return t("NewElement.Number");
                            if (!isNaN(+(value ?? "").toString().replace(",", "."))) return true;
                            else return t("NewElement.Number");
                        } else if (value) {
                            if (!acceptsNegative && (content ?? 0).toString().includes("-"))
                                return "Il n'est pas possible d'indiquer un nombre négatif.";
                            if (/\s/g.test(value)) return t("NewElement.Number");
                            if ((value ?? "").toString().includes(".") || (value ?? "").toString().includes(","))
                                return t("NewElement.NumberInt");
                            if (isNaN(+value)) return t("NewElement.Number");
                            if (!isNaN(+value)) return true;
                        }
                    },
                }}
                render={({ field }) => {
                    return (
                        <>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <label className="form-component-label" htmlFor={id ?? controlName} style={labelStyle}>
                                    {label}
                                    {required && label && "*"}
                                    {showOptionalMark && <TinyBody color="grey">({t("NewElement.Optional")})</TinyBody>}
                                </label>
                                {helpdeskInfo && (
                                    <ExpandedHelpdeskIcon
                                        text={helpdeskInfo?.text}
                                        link={helpdeskInfo?.link}
                                        transparent
                                    />
                                )}
                                {infoTooltip && (
                                    <InfoTooltip
                                        text={infoTooltip.text}
                                        iconType={infoTooltip.type}
                                        link={infoTooltip.link}
                                    />
                                )}
                            </div>
                            <StyledTextField
                                {...field}
                                disabled={disabled}
                                id={id ?? controlName}
                                type="number"
                                error={!!error}
                                name={controlName}
                                fullWidth
                                placeholder={placeholder ?? ""}
                                helperText={customErrorMessage || error?.message || ""}
                                onWheel={(e) => (e.target as HTMLElement).blur()}
                                disabledText={disabledText}
                                hideDisabledLockIcon={hideDisabledLockIcon}
                                onFocus={onFocus}
                                sx={sx}
                                loading={loading}
                                icon={icon}
                                ref={ref}
                                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                    const parsedValue = parseNumber(e.target.value);
                                    onBlur
                                        ? onBlur(parsedValue)
                                        : onBlurWithEvent
                                          ? onBlurWithEvent(e)
                                          : field.onBlur();
                                }}
                                onChange={(e) => {
                                    const parsedValue = parseNumber(e.target.value);
                                    onChange
                                        ? onChange(parsedValue)
                                        : onChangeWithEvent
                                          ? onChangeWithEvent(e)
                                          : field.onChange(parsedValue);
                                }}
                            />
                            {description && <span className="input-description">{description}</span>}
                        </>
                    );
                }}
            />
        </div>
    );
});

export default NumberFormComponent;
